'use client';

import React, { useState, useRef, useEffect, useCallback } from 'react';
import { Input, Button, Tag, Spin, Avatar } from 'antd';
import { useTripChatContext } from '../contexts/TripChatContext';
import {
  SendOutlined,
  MessageOutlined,
  UserOutlined,
  EnvironmentOutlined,
  StarFilled,
  CloseOutlined,
  ExpandOutlined,
  CompressOutlined,
  ShoppingOutlined,
  GoogleOutlined,
  FacebookOutlined,
  LoginOutlined,
  HeartOutlined,
  HeartFilled,
  ClearOutlined,
} from '@ant-design/icons';

const API_BASE = (process.env.NEXT_PUBLIC_API_URL || 'https://app.trip-planner.com.au') + '/api';

// ── Brand colours — match site's blue palette ────────────────────
const BRAND = '#0f172a';       // slate-900 (header, bubble, user bubbles)
const BRAND_DARK = '#020617';  // slate-950 (hover)
const ACCENT = '#0066CC';      // brand blue (send button, suggestion chips, prices)

// ── Types ────────────────────────────────────────────────────────

interface ChatMessage {
  id: string;
  role: 'user' | 'assistant';
  content: string;
  timestamp: Date;
  data?: StructuredData;
  allData?: StructuredData[];
}

interface StructuredData {
  type: 'search_results' | 'campsite_details' | 'campsite_details_list' | 'availability' | 'product_results' | 'order_tracking';
  payload: any;
}

interface CampsiteDetail {
  id: number;
  name: string;
  description?: string;
  short_description?: string;
  rating?: number;
  image_url?: string;
  booking_url?: string;
  url?: string;
  features?: string[];
  address?: {
    street?: string;
    town?: string;
    state?: string;
    postcode?: string;
    latitude?: number;
    longitude?: number;
    phone?: string;
  };
  is_premium?: boolean;
}

interface CampsiteResult {
  id: number;
  name: string;
  short_description?: string;
  rating?: number;
  image_url?: string;
  town?: string;
  state?: string;
  distance_km?: number;
  features?: string[];
  is_premium?: boolean;
  url?: string;
  booking_url?: string;
}

interface ProductResult {
  title: string;
  handle: string;
  image_url?: string;
  vendor?: string;
  product_type?: string;
  price?: number;
  compare_at_price?: number;
  product_url?: string;
}

interface OrderTrackingResult {
  found: boolean;
  order_number?: string;
  order_date?: string;
  status?: string;
  items?: Array<{ title: string; quantity: number }>;
  shipping_location?: string;
  tracking?: Array<{
    carrier: string;
    tracking_number: string;
    tracking_url: string;
  }>;
  account_url?: string;
  message?: string;
}

interface SessionContext {
  vehicle_type?: string;
  vehicle_length?: string;
  group_size?: string;
  has_pets?: string;
  page_context?: string;
}

export interface PageContext {
  pageType: 'campsite' | 'search' | 'home' | 'product' | 'general';
  entityId?: number;
  entityName?: string;
  bookingUrl?: string;
  productType?: string;
  productName?: string;
}

// ── Component ────────────────────────────────────────────────────

interface TripChatProps {
  defaultExpanded?: boolean;
  className?: string;
  initialContext?: SessionContext;
  pageContext?: PageContext;
}

function getSuggestions(ctx?: PageContext): string[] {
  if (!ctx) {
    return [
      'Dog-friendly campsite near Sydney',
      'Caravan park with powered sites in QLD',
      'Free camping near the coast',
      'Track my order',
    ];
  }
  switch (ctx.pageType) {
    case 'campsite':
      return [
        ctx.bookingUrl
          ? (ctx.entityName ? `Check availability at ${ctx.entityName}` : 'Check availability here')
          : (ctx.entityName ? `How do I book ${ctx.entityName}?` : 'How do I book this campsite?'),
        'What gear do I need for this campsite?',
        'Find similar campsites nearby',
        'Is this campsite dog-friendly?',
      ];
    case 'product':
      return [
        'Where can I use this gear?',
        'Find similar products',
        ctx.productName ? `Is ${ctx.productName} good for beach camping?` : 'Is this good for beach camping?',
        'What campsites suit this gear?',
      ];
    default:
      return [
        'Dog-friendly campsite near Sydney',
        'Caravan park with powered sites in QLD',
        'Free camping near the coast',
        'Track my order',
      ];
  }
}

// ── Option extraction helper ──────────────────────────────────
// Detects bullet-point option lists in assistant messages and extracts
// them as clickable choices, leaving the rest of the text clean.

interface ExtractedOptions {
  textBefore: string;
  options: string[];
  textAfter: string;
}

function extractOptions(content: string): ExtractedOptions | null {
  // Find the LAST bullet list in the message — options/choices are typically
  // at the end of a message, while earlier bullet lists are descriptions.
  const lines = content.split('\n');

  // Collect all bullet list blocks
  const blocks: Array<{ start: number; end: number; items: string[] }> = [];
  let currentBlock: { start: number; end: number; items: string[] } | null = null;

  for (let i = 0; i < lines.length; i++) {
    const trimmed = lines[i].trim();
    if (/^[-•]\s+/.test(trimmed)) {
      if (!currentBlock) {
        currentBlock = { start: i, end: i, items: [] };
      }
      currentBlock.end = i;
      // Extract option text: strip bullet, bold markers, and trailing description
      let opt = trimmed.replace(/^[-•]\s+/, '');
      const boldMatch = opt.match(/^\*\*(.+?)\*\*/);
      if (boldMatch) {
        opt = boldMatch[1];
      } else {
        const parenIdx = opt.indexOf('(');
        const dashIdx = opt.indexOf(' — ');
        const colonIdx = opt.indexOf(': ');
        const cutoff = Math.min(
          parenIdx > 0 ? parenIdx : 999,
          dashIdx > 0 ? dashIdx : 999,
          colonIdx > 0 ? colonIdx : 999,
        );
        if (cutoff < 999) opt = opt.substring(0, cutoff);
      }
      currentBlock.items.push(opt.trim());
    } else if (currentBlock && trimmed === '') {
      continue; // Allow blank lines within a block
    } else if (currentBlock) {
      if (currentBlock.items.length >= 2) blocks.push(currentBlock);
      currentBlock = null;
    }
  }
  // Don't forget the last block if message ends with bullets
  if (currentBlock && currentBlock.items.length >= 2) blocks.push(currentBlock);

  // Use the LAST block with 2+ items — that's the one with choices
  if (blocks.length === 0) return null;
  const block = blocks[blocks.length - 1];

  const textBefore = lines.slice(0, block.start).join('\n').trim();
  const textAfter = lines.slice(block.end + 1).join('\n').trim();

  return { textBefore, options: block.items, textAfter };
}

// ── Parse markdown-style links and render booking URLs as styled cards ──
function renderMessageContent(content: string): React.ReactNode {
  // Split content on markdown links: [text](url)
  const linkRegex = /\[([^\]]+)\]\((https?:\/\/[^\s)]+)\)/g;
  const parts: React.ReactNode[] = [];
  let lastIndex = 0;
  let match: RegExpExecArray | null;

  while ((match = linkRegex.exec(content)) !== null) {
    // Add text before the link
    if (match.index > lastIndex) {
      parts.push(
        <React.Fragment key={`t-${lastIndex}`}>
          {renderBoldText(content.slice(lastIndex, match.index))}
        </React.Fragment>
      );
    }

    const linkText = match[1];
    const url = match[2];
    const isBookingLink = url.includes('bookeasy.com') || url.includes('booking');

    if (isBookingLink) {
      // Render as a styled booking card/button
      parts.push(
        <a
          key={`link-${match.index}`}
          href={url}
          target="_blank"
          rel="noopener noreferrer"
          className="block my-2 no-underline"
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 8,
            padding: '10px 14px',
            background: 'linear-gradient(135deg, #0066CC 0%, #0052A3 100%)',
            color: '#fff',
            borderRadius: 10,
            fontSize: 13,
            fontWeight: 600,
            textDecoration: 'none',
            boxShadow: '0 2px 6px rgba(22,100,58,0.3)',
            transition: 'transform 0.15s, box-shadow 0.15s',
          }}
          onMouseEnter={e => {
            e.currentTarget.style.transform = 'translateY(-1px)';
            e.currentTarget.style.boxShadow = '0 4px 12px rgba(22,100,58,0.4)';
          }}
          onMouseLeave={e => {
            e.currentTarget.style.transform = 'none';
            e.currentTarget.style.boxShadow = '0 2px 6px rgba(22,100,58,0.3)';
          }}
        >
          <span style={{ fontSize: 18 }}>🔗</span>
          <span>{linkText}</span>
          <span style={{ marginLeft: 'auto', opacity: 0.8, fontSize: 11 }}>↗</span>
        </a>
      );
    } else {
      // Regular link — render as inline styled anchor
      parts.push(
        <a
          key={`link-${match.index}`}
          href={url}
          target="_blank"
          rel="noopener noreferrer"
          style={{ color: '#0066CC', textDecoration: 'underline' }}
        >
          {linkText}
        </a>
      );
    }

    lastIndex = match.index + match[0].length;
  }

  // Add remaining text
  if (lastIndex < content.length) {
    parts.push(
      <React.Fragment key={`t-${lastIndex}`}>
        {renderBoldText(content.slice(lastIndex))}
      </React.Fragment>
    );
  }

  return parts.length > 0 ? parts : renderBoldText(content);
}

// ── Render **bold** text ──
function renderBoldText(text: string): React.ReactNode {
  const boldRegex = /\*\*(.+?)\*\*/g;
  const parts: React.ReactNode[] = [];
  let lastIdx = 0;
  let m: RegExpExecArray | null;

  while ((m = boldRegex.exec(text)) !== null) {
    if (m.index > lastIdx) {
      parts.push(text.slice(lastIdx, m.index));
    }
    parts.push(<strong key={`b-${m.index}`}>{m[1]}</strong>);
    lastIdx = m.index + m[0].length;
  }

  if (lastIdx < text.length) {
    parts.push(text.slice(lastIdx));
  }

  return parts.length > 0 ? parts : text;
}

export default function TripChat({
  defaultExpanded = false,
  className = '',
  initialContext,
  pageContext: pageContextProp,
}: TripChatProps) {
  const { pageContext: pageContextFromProvider } = useTripChatContext();
  const pageContext = pageContextProp ?? pageContextFromProvider;

  const [isOpen, setIsOpen] = useState(defaultExpanded);
  const [isExpanded, setIsExpanded] = useState(false);
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [input, setInput] = useState('');
  const [isLoading, setIsLoading] = useState(false);

  // Detect whether the MobileCTABar is present (sets data-mobile-cta on <html>)
  const [hasMobileCTA, setHasMobileCTA] = useState(false);
  useEffect(() => {
    const check = () =>
      setHasMobileCTA(document.documentElement.hasAttribute('data-mobile-cta'));
    check();
    const observer = new MutationObserver(check);
    observer.observe(document.documentElement, { attributes: true, attributeFilter: ['data-mobile-cta'] });
    return () => observer.disconnect();
  }, []);
  const [sessionId, setSessionId] = useState<string | null>(() => {
    // Restore session ID from localStorage on mount
    if (typeof window !== 'undefined') {
      return localStorage.getItem('campingAssistantSessionId');
    }
    return null;
  });
  const [statusText, setStatusText] = useState('');
  const [loginRequired, setLoginRequired] = useState(false);
  const [sessionRestored, setSessionRestored] = useState(false);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  // Clear chat and start a new session
  const clearChat = useCallback(() => {
    setMessages([]);
    setInput('');
    setStatusText('');
    setSessionId(null);
    setSessionRestored(false);
    setLoginRequired(false);
    if (typeof window !== 'undefined') {
      localStorage.removeItem('campingAssistantSessionId');
    }
    // Focus the input after clearing
    setTimeout(() => inputRef.current?.focus(), 100);
  }, []);

  // Persist sessionId to localStorage whenever it changes
  useEffect(() => {
    if (typeof window === 'undefined') return;
    if (sessionId) {
      localStorage.setItem('campingAssistantSessionId', sessionId);
    }
  }, [sessionId]);

  // Restore conversation history from backend when we have a sessionId
  // but no messages (i.e. after a page reload / OAuth redirect)
  useEffect(() => {
    if (!sessionId || messages.length > 0 || sessionRestored) return;
    setSessionRestored(true);

    (async () => {
      try {
        const res = await fetch(`${API_BASE}/agents/session/${sessionId}`);
        if (!res.ok) {
          // Session expired or doesn't exist — clear it
          localStorage.removeItem('campingAssistantSessionId');
          setSessionId(null);
          return;
        }
        const data = await res.json();
        if (data.history && data.history.length > 0) {
          const restored: ChatMessage[] = data.history.map(
            (msg: { role: string; content: string }, i: number) => ({
              id: `restored-${i}`,
              role: msg.role as 'user' | 'assistant',
              content: msg.content,
              timestamp: new Date(),
            })
          );
          setMessages(restored);
          // Auto-open chat if we have history
          setIsOpen(true);
        }
      } catch {
        // Silently fail — fresh session will be created on next message
      }
    })();
  }, [sessionId, messages.length, sessionRestored]);

  // Auto-scroll to bottom on new messages
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages, statusText]);

  // Focus input when chat opens
  useEffect(() => {
    if (isOpen) {
      setTimeout(() => inputRef.current?.focus(), 100);
    }
  }, [isOpen]);

  // ── Auth helpers ──────────────────────────────────────────────

  function getReviewerToken(): string | null {
    if (typeof window === 'undefined') return null;
    return localStorage.getItem('reviewerToken');
  }

  function isLoggedIn(): boolean {
    return !!getReviewerToken();
  }

  function handleLogin(provider: 'google' | 'facebook') {
    const redirectUrl = typeof window !== 'undefined' ? window.location.href : '';
    window.location.href = `${API_BASE}/reviewer/auth/${provider}/login?redirect_to=${encodeURIComponent(redirectUrl)}`;
  }

  // ── Session Management ──────────────────────────────────────

  const ensureSession = useCallback(async (): Promise<string> => {
    if (sessionId) return sessionId;

    // Check localStorage in case state hasn't synced yet
    const stored = typeof window !== 'undefined'
      ? localStorage.getItem('campingAssistantSessionId')
      : null;
    if (stored) {
      setSessionId(stored);
      return stored;
    }

    const ctx: SessionContext = { ...initialContext };
    if (pageContext) {
      ctx.page_context = JSON.stringify(pageContext);
    }

    try {
      const res = await fetch(`${API_BASE}/agents/session`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ context: ctx }),
      });
      const data = await res.json();
      setSessionId(data.session_id);
      return data.session_id;
    } catch (err) {
      console.error('Failed to create session:', err);
      throw err;
    }
  }, [sessionId, initialContext, pageContext]);

  // ── Send Message ────────────────────────────────────────────

  const sendMessage = useCallback(async (overrideText?: string) => {
    const text = (overrideText || input).trim();
    if (!text || isLoading) return;

    setInput('');
    setIsLoading(true);
    setStatusText('Thinking...');
    setLoginRequired(false);

    const userMsg: ChatMessage = {
      id: `user-${Date.now()}`,
      role: 'user',
      content: text,
      timestamp: new Date(),
    };
    setMessages(prev => [...prev, userMsg]);

    try {
      const sid = await ensureSession();

      const headers: Record<string, string> = {
        'Content-Type': 'application/json',
      };
      const token = getReviewerToken();
      if (token) {
        headers['Authorization'] = `Bearer ${token}`;
      }

      const res = await fetch(`${API_BASE}/agents/session/${sid}/stream`, {
        method: 'POST',
        headers,
        body: JSON.stringify({
          content: text,
          task_type: 'conversation',
        }),
      });

      if (!res.ok) {
        throw new Error(`Server error: ${res.status}`);
      }

      const reader = res.body?.getReader();
      const decoder = new TextDecoder();
      let buffer = '';
      let assistantContent = '';
      const allStructuredData: StructuredData[] = [];

      if (reader) {
        while (true) {
          const { done, value } = await reader.read();
          if (done) break;

          buffer += decoder.decode(value, { stream: true });

          const lines = buffer.split('\n');
          buffer = lines.pop() || '';

          let eventType = '';
          for (const line of lines) {
            if (line.startsWith('event: ')) {
              eventType = line.slice(7).trim();
            } else if (line.startsWith('data: ') && eventType) {
              try {
                const eventData = JSON.parse(line.slice(6));
                switch (eventType) {
                  case 'status':
                    setStatusText(eventData.message || 'Processing...');
                    break;
                  case 'text':
                    assistantContent = eventData.content || '';
                    break;
                  case 'data':
                    allStructuredData.push({
                      type: eventData.type,
                      payload: eventData.payload,
                    });
                    break;
                  case 'done':
                    setStatusText('');
                    break;
                  case 'error':
                    assistantContent = eventData.message || 'Sorry, I\u2019m having trouble right now. Please try again in a moment.';
                    break;
                  case 'login_required':
                    // Save the assistant's text message before showing login wall
                    if (assistantContent) {
                      setMessages(prev => [...prev, {
                        id: `assistant-${Date.now()}`,
                        role: 'assistant',
                        content: assistantContent,
                        timestamp: new Date(),
                      }]);
                      assistantContent = '';
                    }
                    setLoginRequired(true);
                    setIsLoading(false);
                    setStatusText('');
                    return;
                }
              } catch {
                // skip malformed JSON
              }
              eventType = '';
            }
          }
        }
      }

      const assistantMsg: ChatMessage = {
        id: `assistant-${Date.now()}`,
        role: 'assistant',
        content: assistantContent || 'I couldn\'t process that request. Please try again.',
        timestamp: new Date(),
        data: allStructuredData.length > 0 ? allStructuredData[0] : undefined,
        allData: allStructuredData.length > 0 ? allStructuredData : undefined,
      };
      setMessages(prev => [...prev, assistantMsg]);

    } catch (err) {
      console.error('Chat error:', err);
      setMessages(prev => [
        ...prev,
        {
          id: `error-${Date.now()}`,
          role: 'assistant',
          content: 'Sorry, I\'m having trouble connecting. Please try again in a moment.',
          timestamp: new Date(),
        },
      ]);
    } finally {
      setIsLoading(false);
      setStatusText('');
    }
  }, [input, isLoading, ensureSession]);

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      sendMessage();
    }
  };

  // ── Chat Bubble (collapsed state) ──────────────────────────

  // On mobile, shift up above the MobileCTABar when it's present (bar ≈ 80px)
  const bubbleBottom = hasMobileCTA ? 'bottom-[96px] md:bottom-6' : 'bottom-6';

  if (!isOpen) {
    return (
      <div className={`fixed ${bubbleBottom} right-4 md:right-6 z-50 ${className}`}>
        <button
          onClick={() => setIsOpen(true)}
          className={`flex items-center gap-2 px-5 py-3 text-white rounded-full shadow-lg transition-all hover:scale-105`}
          style={{ background: BRAND }}
          onMouseEnter={e => (e.currentTarget.style.background = BRAND_DARK)}
          onMouseLeave={e => (e.currentTarget.style.background = BRAND)}
        >
          <MessageOutlined className="text-lg" />
          <span className="font-semibold text-sm">Camping Assistant</span>
        </button>
      </div>
    );
  }

  // ── Chat Panel (open state) ────────────────────────────────

  const panelClasses = isExpanded
    ? 'fixed inset-4 z-50'
    : `fixed ${bubbleBottom} right-4 md:right-6 z-50 w-[420px] h-[600px] max-h-[80vh]`;

  const suggestions = getSuggestions(pageContext);

  return (
    <div className={`${panelClasses} ${className}`}>
      <div className="flex flex-col h-full bg-white rounded-2xl shadow-2xl border border-gray-200 overflow-hidden">
        {/* Header */}
        <div
          className="flex items-center justify-between px-4 py-3 text-white"
          style={{ background: BRAND }}
        >
          <div className="flex items-center gap-2">
            <Avatar
              size={32}
              icon={<MessageOutlined />}
              className="bg-white/20"
            />
            <div>
              <div className="font-semibold text-sm leading-tight">Camping Assistant</div>
              <div className="text-[11px] text-white/70">Powered by AI</div>
            </div>
          </div>
          <div className="flex items-center gap-1">
            {messages.length > 0 && (
              <button
                onClick={clearChat}
                className="p-1.5 rounded-md text-white/70 hover:text-white hover:bg-white/10 transition-colors"
                title="New conversation"
              >
                <ClearOutlined />
              </button>
            )}
            <button
              onClick={() => setIsExpanded(!isExpanded)}
              className="p-1.5 rounded-md text-white/70 hover:text-white hover:bg-white/10 transition-colors"
            >
              {isExpanded ? <CompressOutlined /> : <ExpandOutlined />}
            </button>
            <button
              onClick={() => setIsOpen(false)}
              className="p-1.5 rounded-md text-white/70 hover:text-white hover:bg-white/10 transition-colors"
            >
              <CloseOutlined />
            </button>
          </div>
        </div>

        {/* Messages */}
        <div className="flex-1 overflow-y-auto px-4 py-3 space-y-4 bg-gray-50">
          {messages.length === 0 && (
            <div className="text-center py-8">
              <MessageOutlined className="text-4xl mb-3 text-slate-300" />
              <p className="text-gray-500 text-sm mb-4">
                G&apos;day! I&apos;m your Camping Assistant. I can help you find campsites, check availability, recommend gear, or answer any questions about your order.
              </p>
              <div className="flex flex-wrap justify-center gap-2">
                {suggestions.map(suggestion => (
                  <button
                    key={suggestion}
                    onClick={() => {
                      setInput(suggestion);
                      sendMessage(suggestion);
                    }}
                    className="text-xs px-3 py-1.5 rounded-full transition-colors"
                    style={{
                      background: `${ACCENT}18`,
                      color: ACCENT,
                    }}
                    onMouseEnter={e => (e.currentTarget.style.background = `${ACCENT}30`)}
                    onMouseLeave={e => (e.currentTarget.style.background = `${ACCENT}18`)}
                  >
                    {suggestion}
                  </button>
                ))}
              </div>
            </div>
          )}

          {messages.map((msg, msgIdx) => {
            // Extract clickable options from assistant messages
            const extracted = msg.role === 'assistant' && !isLoading
              ? extractOptions(msg.content)
              : null;
            // Only show option buttons on the LAST assistant message
            const isLastAssistant = msg.role === 'assistant'
              && msgIdx === messages.map(m => m.role).lastIndexOf('assistant');
            const showOptions = extracted && isLastAssistant;

            return (
              <div key={msg.id}>
                {/* Message bubble */}
                <div className={`flex gap-2 ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`}>
                  {msg.role === 'assistant' && (
                    <Avatar size={28} icon={<MessageOutlined />} style={{ background: BRAND }} className="flex-shrink-0 mt-0.5" />
                  )}
                  <div
                    className={`max-w-[85%] px-3 py-2 rounded-2xl text-sm leading-relaxed ${
                      msg.role === 'user'
                        ? 'text-white rounded-br-md'
                        : 'bg-white text-gray-800 border border-gray-200 rounded-bl-md shadow-sm'
                    }`}
                    style={msg.role === 'user' ? { background: BRAND } : undefined}
                  >
                    <div className="whitespace-pre-wrap">
                      {showOptions ? (
                        <>
                          {renderMessageContent(extracted.textBefore)}
                          {extracted.textAfter && <>{'\n\n'}{renderMessageContent(extracted.textAfter)}</>}
                        </>
                      ) : (
                        msg.role === 'assistant' ? renderMessageContent(msg.content) : msg.content
                      )}
                    </div>
                  </div>
                  {msg.role === 'user' && (
                    <Avatar size={28} icon={<UserOutlined />} className="bg-gray-400 flex-shrink-0 mt-0.5" />
                  )}
                </div>

                {/* Clickable option buttons */}
                {showOptions && (
                  <div className="mt-2 ml-9 flex flex-wrap gap-2">
                    {extracted.options.map(opt => (
                      <button
                        key={opt}
                        onClick={() => sendMessage(opt)}
                        disabled={isLoading}
                        className="text-xs px-3 py-1.5 rounded-full border transition-colors"
                        style={{
                          borderColor: ACCENT,
                          color: ACCENT,
                          background: `${ACCENT}10`,
                        }}
                        onMouseEnter={e => {
                          e.currentTarget.style.background = ACCENT;
                          e.currentTarget.style.color = '#fff';
                        }}
                        onMouseLeave={e => {
                          e.currentTarget.style.background = `${ACCENT}10`;
                          e.currentTarget.style.color = ACCENT;
                        }}
                      >
                        {opt}
                      </button>
                    ))}
                    <button
                      onClick={() => inputRef.current?.focus()}
                      className="text-xs px-3 py-1.5 rounded-full border border-gray-300 text-gray-500 hover:border-gray-400 hover:text-gray-700 transition-colors"
                    >
                      Other...
                    </button>
                  </div>
                )}

                {/* Render ALL structured data attached to this message */}
                {(msg.allData || (msg.data ? [msg.data] : [])).map((sd, sdIdx) => {
                  if (sd.type === 'search_results' && sd.payload?.campsites) {
                    return (
                      <div key={`sd-${sdIdx}`} className="mt-3 space-y-2 ml-9">
                        {sd.payload.campsites.slice(0, 6).map((site: CampsiteResult) => (
                          <CampsiteCard key={site.id} site={site} />
                        ))}
                      </div>
                    );
                  }
                  if (sd.type === 'campsite_details_list' && Array.isArray(sd.payload)) {
                    return (
                      <div key={`sd-${sdIdx}`} className="mt-3 space-y-2 ml-9">
                        {(sd.payload as CampsiteDetail[]).map((detail) => (
                          <CampsiteDetailCard key={detail.id} detail={detail} />
                        ))}
                      </div>
                    );
                  }
                  if (sd.type === 'product_results' && sd.payload?.products) {
                    return (
                      <div key={`sd-${sdIdx}`} className="mt-3 space-y-2 ml-9">
                        {sd.payload.products.slice(0, 6).map((product: ProductResult) => (
                          <ProductCard key={product.handle} product={product} />
                        ))}
                      </div>
                    );
                  }
                  if (sd.type === 'order_tracking' && sd.payload) {
                    return (
                      <div key={`sd-${sdIdx}`} className="mt-3 ml-9">
                        <OrderTrackingCard order={sd.payload as OrderTrackingResult} />
                      </div>
                    );
                  }
                  return null;
                })}
              </div>
            );
          })}

          {/* Loading indicator */}
          {isLoading && (
            <div className="flex gap-2 items-start">
              <Avatar size={28} icon={<MessageOutlined />} style={{ background: BRAND }} className="flex-shrink-0" />
              <div className="bg-white border border-gray-200 rounded-2xl rounded-bl-md px-4 py-3 shadow-sm">
                <div className="flex items-center gap-2 text-gray-500 text-sm">
                  <Spin size="small" />
                  <span>{statusText || 'Thinking...'}</span>
                </div>
              </div>
            </div>
          )}

          {/* Login wall — shown when user tries a trip-planning action */}
          {loginRequired && (
            <div className="bg-white border border-blue-200 rounded-2xl p-5 shadow-sm text-center">
              <LoginOutlined className="text-3xl mb-2" style={{ color: ACCENT }} />
              <p className="text-sm font-semibold text-gray-800 mb-1">
                Sign in to check availability &amp; plan your trip
              </p>
              <p className="text-xs text-gray-500 mb-4">
                Create a free account to unlock availability checks, booking holds, and trip planning features.
              </p>
              <div className="flex flex-col gap-2">
                <button
                  onClick={() => handleLogin('google')}
                  className="flex items-center justify-center gap-2 px-4 py-2.5 bg-white border border-gray-300 rounded-lg text-sm font-medium text-gray-700 hover:bg-gray-50 transition-colors"
                >
                  <GoogleOutlined />
                  Continue with Google
                </button>
                <button
                  onClick={() => handleLogin('facebook')}
                  className="flex items-center justify-center gap-2 px-4 py-2.5 bg-[#1877F2] text-white rounded-lg text-sm font-medium hover:bg-[#166FE5] transition-colors"
                >
                  <FacebookOutlined />
                  Continue with Facebook
                </button>
              </div>
              <button
                onClick={() => setLoginRequired(false)}
                className="mt-3 text-xs text-gray-400 hover:text-gray-600 transition-colors"
              >
                Maybe later — keep browsing
              </button>
            </div>
          )}

          <div ref={messagesEndRef} />
        </div>

        {/* Input */}
        <div className="px-4 py-3 bg-white border-t border-gray-200">
          <div className="flex gap-2">
            <Input
              ref={inputRef as any}
              value={input}
              onChange={e => setInput(e.target.value)}
              onKeyDown={handleKeyDown}
              placeholder="Ask about campsites or gear..."
              disabled={isLoading}
              className="rounded-full"
              size="large"
            />
            <Button
              type="primary"
              icon={<SendOutlined />}
              onClick={() => sendMessage()}
              loading={isLoading}
              disabled={!input.trim()}
              className="rounded-full border-none"
              style={{ background: '#10b981' }}
              size="large"
            />
          </div>
        </div>
      </div>
    </div>
  );
}

// ── Campsite Card Component ──────────────────────────────────

function CampsiteCard({ site }: { site: CampsiteResult }) {
  // Ensure URL always includes Shopify app proxy prefix
  const detailUrl = (site.url && site.url.includes('/apps/campsite-explorer/'))
    ? site.url
    : `/apps/campsite-explorer/camp-sites/${site.id}`;
  return (
    <div className="bg-white rounded-xl border border-gray-200 overflow-hidden hover:shadow-md transition-shadow">
      <a
        href={detailUrl}
        target="_blank"
        rel="noopener noreferrer"
        className="flex no-underline text-inherit"
      >
        <div className="w-24 h-24 flex-shrink-0 bg-gray-100 relative">
          {site.image_url ? (
            <img
              src={site.image_url}
              alt={site.name}
              className="w-full h-full object-cover"
              loading="lazy"
            />
          ) : (
            <div className="w-full h-full flex items-center justify-center bg-gradient-to-br from-blue-100 to-blue-200">
              <EnvironmentOutlined className="text-2xl text-blue-500/40" />
            </div>
          )}
          {site.is_premium && (
            <span className="absolute top-1 left-1 px-1.5 py-0.5 rounded text-[9px] font-bold text-amber-900 bg-amber-400/90">
              PREMIUM
            </span>
          )}
        </div>
        <div className="flex-1 p-2.5 min-w-0">
          <h4 className="text-sm font-semibold text-gray-900 truncate mb-0.5">
            {site.name}
          </h4>
          <div className="flex items-center gap-1 text-xs text-gray-500 mb-1">
            <EnvironmentOutlined className="text-[10px]" />
            <span className="truncate">
              {[site.town, site.state].filter(Boolean).join(', ')}
              {site.distance_km ? ` (${site.distance_km}km)` : ''}
            </span>
          </div>
          {site.rating && !isNaN(Number(site.rating)) && Number(site.rating) > 0 && (
            <div className="flex items-center gap-1 mb-1">
              <StarFilled className="text-amber-500 text-[10px]" />
              <span className="text-xs font-medium">{Number(site.rating).toFixed(1)}</span>
            </div>
          )}
          {site.features && site.features.length > 0 && (
            <div className="flex flex-wrap gap-1">
              {site.features.slice(0, 3).map(f => (
                <Tag
                  key={f}
                  className="text-[9px] px-1.5 py-0 border-0 bg-gray-100 text-gray-600 m-0"
                >
                  {f}
                </Tag>
              ))}
              {site.features.length > 3 && (
                <span className="text-[9px] text-gray-400">+{site.features.length - 3}</span>
              )}
            </div>
          )}
        </div>
      </a>
      {/* Action bar with booking button when available */}
      <div className="px-3 py-1.5 border-t border-gray-100 flex justify-end items-center gap-2">
        {site.booking_url && (
          <a
            href={site.booking_url}
            target="_blank"
            rel="noopener noreferrer"
            className="text-[10px] font-semibold no-underline px-2.5 py-1 rounded-full text-white"
            style={{ background: ACCENT }}
          >
            Book now
          </a>
        )}
        <a
          href={detailUrl}
          target="_blank"
          rel="noopener noreferrer"
          className="text-[10px] font-medium no-underline"
          style={{ color: ACCENT }}
        >
          View details →
        </a>
      </div>
    </div>
  );
}

// ── Product Card Component ───────────────────────────────────

function ProductCard({ product }: { product: ProductResult }) {
  const priceStr = product.price
    ? `$${product.price.toFixed(2)}`
    : '';
  const compareStr = product.compare_at_price && product.compare_at_price > (product.price || 0)
    ? `$${product.compare_at_price.toFixed(2)}`
    : '';

  return (
    <a
      href={product.product_url || '#'}
      target="_blank"
      rel="noopener noreferrer"
      className="block bg-white rounded-xl border border-gray-200 overflow-hidden hover:shadow-md transition-shadow no-underline text-inherit"
    >
      <div className="flex">
        <div className="w-24 h-24 flex-shrink-0 bg-gray-100 relative">
          {product.image_url ? (
            <img
              src={product.image_url}
              alt={product.title}
              className="w-full h-full object-cover"
              loading="lazy"
            />
          ) : (
            <div className="w-full h-full flex items-center justify-center bg-gradient-to-br from-blue-100 to-blue-200">
              <ShoppingOutlined className="text-2xl text-blue-600/40" />
            </div>
          )}
        </div>
        <div className="flex-1 p-2.5 min-w-0">
          <h4 className="text-sm font-semibold text-gray-900 truncate mb-0.5">
            {product.title}
          </h4>
          {product.vendor && (
            <div className="text-xs text-gray-500 mb-1 truncate">
              {product.vendor}
            </div>
          )}
          <div className="flex items-center gap-2 mb-1">
            {priceStr && (
              <span className="text-sm font-bold" style={{ color: ACCENT }}>
                {priceStr}
              </span>
            )}
            {compareStr && (
              <span className="text-xs text-gray-400 line-through">
                {compareStr}
              </span>
            )}
          </div>
          <div className="text-xs font-medium" style={{ color: ACCENT }}>
            View product →
          </div>
        </div>
      </div>
    </a>
  );
}

// ── Save helper — reuses existing localStorage savedCampsites ──
function useSavedState(url: string) {
  const [isSaved, setIsSaved] = useState(false);
  useEffect(() => {
    if (typeof window === 'undefined') return;
    const saved = JSON.parse(localStorage.getItem('savedCampsites') || '[]');
    setIsSaved(saved.some((c: any) => c.url === url));
  }, [url]);

  const toggle = (name: string, lat?: number, lng?: number, phone?: string, bookingUrl?: string) => {
    const saved = JSON.parse(localStorage.getItem('savedCampsites') || '[]');
    if (isSaved) {
      localStorage.setItem('savedCampsites', JSON.stringify(saved.filter((c: any) => c.url !== url)));
      setIsSaved(false);
    } else {
      saved.push({
        name,
        url,
        savedAt: new Date().toISOString(),
        latitude: lat ? String(lat) : undefined,
        longitude: lng ? String(lng) : undefined,
        phone: phone || undefined,
        booking_url: bookingUrl || undefined,
      });
      localStorage.setItem('savedCampsites', JSON.stringify(saved));
      setIsSaved(true);
    }
  };

  return { isSaved, toggle };
}

// ── Campsite Detail Card (from get_campsite_details) ──────────

function CampsiteDetailCard({ detail }: { detail: CampsiteDetail }) {
  const slug = detail.name?.toLowerCase()
    .replace(/'/g, '').replace(/"/g, '').replace(/&/g, 'and')
    .replace(/[^a-z0-9 -]/g, '')
    .trim().replace(/\s+/g, '-').replace(/--+/g, '-') || '';
  const slugUrl = `https://campingaustralia.com.au/apps/campsite-explorer/camp-sites/${slug}`;
  // Ensure the URL always includes the Shopify app proxy prefix
  const url = (detail.url && detail.url.includes('/apps/campsite-explorer/'))
    ? detail.url
    : slugUrl;
  const { isSaved, toggle } = useSavedState(url);
  const town = detail.address?.town || '';
  const state = detail.address?.state || '';
  const location = [town, state].filter(Boolean).join(', ');

  return (
    <div className="bg-white rounded-xl border border-gray-200 overflow-hidden hover:shadow-md transition-shadow">
      <a
        href={url}
        target="_blank"
        rel="noopener noreferrer"
        className="flex no-underline text-inherit"
      >
        <div className="w-24 h-24 flex-shrink-0 bg-gray-100 relative">
          {detail.image_url ? (
            <img src={detail.image_url} alt={detail.name} className="w-full h-full object-cover" loading="lazy" />
          ) : (
            <div className="w-full h-full flex items-center justify-center bg-gradient-to-br from-blue-100 to-blue-200">
              <EnvironmentOutlined className="text-2xl text-blue-500/40" />
            </div>
          )}
          {detail.is_premium && (
            <span className="absolute top-1 left-1 px-1.5 py-0.5 rounded text-[9px] font-bold text-amber-900 bg-amber-400/90">
              PREMIUM
            </span>
          )}
        </div>
        <div className="flex-1 p-2.5 min-w-0">
          <div className="flex items-start justify-between gap-1">
            <h4 className="text-sm font-semibold text-gray-900 truncate mb-0.5">{detail.name}</h4>
          </div>
          {location && (
            <div className="flex items-center gap-1 text-xs text-gray-500 mb-1">
              <EnvironmentOutlined className="text-[10px]" />
              <span className="truncate">{location}</span>
            </div>
          )}
          {detail.rating && !isNaN(Number(detail.rating)) && Number(detail.rating) > 0 && (
            <div className="flex items-center gap-1 mb-1">
              <StarFilled className="text-amber-500 text-[10px]" />
              <span className="text-xs font-medium">{Number(detail.rating).toFixed(1)}</span>
            </div>
          )}
          {detail.features && detail.features.length > 0 && (
            <div className="flex flex-wrap gap-1">
              {detail.features.slice(0, 3).map(f => (
                <Tag key={f} className="text-[9px] px-1.5 py-0 border-0 bg-gray-100 text-gray-600 m-0">{f}</Tag>
              ))}
              {detail.features.length > 3 && (
                <span className="text-[9px] text-gray-400">+{detail.features.length - 3}</span>
              )}
            </div>
          )}
        </div>
      </a>
      {/* Action bar */}
      <div className="px-3 py-1.5 border-t border-gray-100 flex justify-between items-center">
        <button
          onClick={(e) => {
            e.stopPropagation();
            toggle(detail.name, detail.address?.latitude, detail.address?.longitude, detail.address?.phone, detail.booking_url);
          }}
          className="flex items-center gap-1 text-xs transition-colors"
          style={{ color: isSaved ? '#ef4444' : '#9ca3af' }}
        >
          {isSaved ? <HeartFilled className="text-red-500" /> : <HeartOutlined />}
          {isSaved ? 'Saved' : 'Save'}
        </button>
        <div className="flex items-center gap-2">
          {detail.booking_url && (
            <a
              href={detail.booking_url}
              target="_blank"
              rel="noopener noreferrer"
              className="text-[10px] font-semibold no-underline px-2.5 py-1 rounded-full text-white"
              style={{ background: ACCENT }}
            >
              Book now
            </a>
          )}
          <a
            href={url}
            target="_blank"
            rel="noopener noreferrer"
            className="text-[10px] font-medium no-underline"
            style={{ color: ACCENT }}
          >
            View details →
          </a>
        </div>
      </div>
    </div>
  );
}

function OrderTrackingCard({ order }: { order: OrderTrackingResult }) {
  if (!order.found) {
    return (
      <div className="bg-white rounded-xl border border-gray-200 p-4 shadow-sm">
        <p className="text-sm text-gray-600 mb-2">{order.message}</p>
        {order.account_url && (
          <a
            href={order.account_url}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-block text-sm font-medium"
            style={{ color: ACCENT }}
          >
            View your account →
          </a>
        )}
      </div>
    );
  }

  return (
    <div className="bg-white rounded-xl border border-gray-200 overflow-hidden shadow-sm">
      {/* Header — order number + status */}
      <div className="px-4 py-3 border-b border-gray-100" style={{ background: `${ACCENT}08` }}>
        <div className="flex justify-between items-center">
          <div>
            <div className="text-[10px] text-gray-500 uppercase tracking-wide">Order</div>
            <div className="text-sm font-bold text-gray-900">{order.order_number}</div>
          </div>
          <div className="text-right">
            {order.order_date && (
              <div className="text-[10px] text-gray-500">{order.order_date}</div>
            )}
            <div className="text-sm font-semibold" style={{ color: ACCENT }}>{order.status}</div>
          </div>
        </div>
      </div>

      {/* Items list */}
      {order.items && order.items.length > 0 && (
        <div className="px-4 py-2 border-b border-gray-100">
          {order.items.map((item, i) => (
            <div key={i} className="flex justify-between text-xs text-gray-700 py-0.5">
              <span className="truncate mr-2">{item.title}</span>
              <span className="text-gray-400 flex-shrink-0">×{item.quantity}</span>
            </div>
          ))}
        </div>
      )}

      {/* Shipping + tracking */}
      <div className="px-4 py-2.5">
        {order.shipping_location && (
          <div className="text-xs text-gray-500 mb-1">
            <EnvironmentOutlined className="mr-1" />
            Shipping to: <span className="text-gray-700 font-medium">{order.shipping_location}</span>
          </div>
        )}
        {order.tracking && order.tracking.map((t, i) => (
          <div key={i} className="text-xs mt-1">
            {t.carrier && <span className="text-gray-500">{t.carrier}: </span>}
            {t.tracking_url ? (
              <a
                href={t.tracking_url}
                target="_blank"
                rel="noopener noreferrer"
                className="font-medium underline"
                style={{ color: ACCENT }}
              >
                {t.tracking_number || 'Track shipment'}
              </a>
            ) : t.tracking_number ? (
              <span className="text-gray-700 font-medium">{t.tracking_number}</span>
            ) : null}
          </div>
        ))}
      </div>

      {/* Action links */}
      <div className="px-4 py-2.5 border-t border-gray-100 flex gap-4">
        {order.tracking && order.tracking.length > 0 && order.tracking[0].tracking_url && (
          <a
            href={order.tracking[0].tracking_url}
            target="_blank"
            rel="noopener noreferrer"
            className="text-xs font-medium"
            style={{ color: ACCENT }}
          >
            Track shipment →
          </a>
        )}
        {order.account_url && (
          <a
            href={order.account_url}
            target="_blank"
            rel="noopener noreferrer"
            className="text-xs font-medium"
            style={{ color: ACCENT }}
          >
            View in your account →
          </a>
        )}
      </div>
    </div>
  );
}
